<template>
  <resize-observer :breakpoints="breakpoints">
    <div slot-scope="{ el }" class="pricing-breakdown-with-currency" :class="el.is">
      <sb-panel>
        <div class="pricing-breakdown-content">
          <div class="panel-row">
            <div class="heading bold">
              Basic Pricing Plan
            </div>
          </div>
          <div class="panel-row">
            <div class="row-icon">
              <img class="cart-icon" src="../../assets/images/cart.svg">
            </div>
            <div class="row-text">
              <div class="column">
                <help-center-link class="help-link" link-key="pricing/fulfillmentCost">
                  Fulfillment Cost
                </help-center-link>
                <div class="hide-large-screen" />
                <em>(B2C Orders with 25 items or less)</em>
              </div>
            </div>
          </div>
          <div class="panel-row no-icon padding-top-0">
            <div class="row-text row-indent">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/pickAndPack">
                  Pick and Pack
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Included in the cost</em>
              </div>
              <div class="column column-3">
                Free for the first {{ freeNumberOfPickInWord }} picks in an order, then <strong>{{ perPickFee | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }} per each additional pick</strong>
              </div>
            </div>
          </div>
          <div class="panel-row no-icon">
            <div class="row-text row-indent">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/standardPackaging">
                  Standard Packaging
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Included in the cost</em>
              </div>
              <div class="column column-3">
                <strong>Free</strong> standard plain boxes, mailers, tape, and dunnage used to pack orders
              </div>
            </div>
          </div>
          <div class="panel-row no-icon">
            <div class="row-text row-indent">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/shipping">
                  Shipping
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Specific to the order</em>
              </div>
              <div class="column column-3">
                <strong>Varies</strong> based on destination, weight, dimensions, shipping service, and more
              </div>
            </div>
          </div>
          <div class="panel-h-separator" />
          <div class="panel-row">
            <div class="row-icon">
              <img class="storage-icon" src="../../assets/images/storage.svg">
            </div>
            <div class="row-text">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/storage">
                  Storage
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Monthly charge</em>
              </div>
              <div class="column column-3">
                <strong>{{ palletStorageFee | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }}</strong> per pallet/month, <strong>{{ shelfStorageFee | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }}</strong> per shelf/month, <strong>{{ binStorageFee | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }}</strong> per bin/month
              </div>
            </div>
          </div>
          <div class="panel-h-separator" />
          <div class="panel-row">
            <div class="row-icon">
              <img class="receiving-icon" src="../../assets/images/receiving.svg">
            </div>
            <div class="row-text">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/receiving">
                  Receiving
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Per Warehouse Receiving Order</em>
              </div>
              <div class="column column-3">
                <strong>{{ wroFeeBeforeHours | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }}</strong> for the first {{ wroFeeAfterHoursInWord }} hours (<strong>{{ wroFeeAfterHours | formatCurrency({currency: homeCountry.currencyCode, showCurrencyCode: showLocalCurrencyWithSymbolAndCode}) }}/man hour</strong> after the first {{ wroFeeAfterHoursInWord }} hours)
              </div>
            </div>
          </div>
          <div class="panel-h-separator" />
          <div class="panel-row">
            <div class="row-icon">
              <img class="additional-services-icon" src="../../assets/images/additional-services.svg">
            </div>
            <div class="row-text">
              <div class="column column-1">
                <help-center-link class="help-link" link-key="pricing/additionalServices">
                  Additional Services
                </help-center-link>
              </div>
              <div class="column column-2">
                <em>Specific to the order</em>
              </div>
              <div class="column column-3">
                Kitting, B2B, large orders (>25 items), returns, surcharges, etc
              </div>
            </div>
          </div>
        </div>
      </sb-panel>
      <div class="note">
        If you're on a custom pricing plan, please refer to it for the most accurate and up-to-date pricing information.
      </div>
    </div>
  </resize-observer>
</template>

<script>
import MerchantCountryCurrency from '@/models/pricing/merchantCountryCurrency'
export default {
  name: 'PricingBreakdownWithCurrency',
  data: () => ({
    breakpoints: {
      small: el => el.width < 600,
      large: el => el.width >= 600
    },
    homeCountry: new MerchantCountryCurrency(),
    numberInWords: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
  }),
  computed: {
    defaultRates () {
      return this.$shipBobStore.getters['pricing/defaultRates']
    },
    palletStorageFee () {
      const palletFee = this.defaultRates.storageFeeConfiguration.find(x => x.actionSourceId === 7)
      return palletFee ? palletFee.amount : 0
    },
    shelfStorageFee () {
      const shelfFee = this.defaultRates.storageFeeConfiguration.find(x => x.actionSourceId === 8)
      return shelfFee ? shelfFee.amount : 0
    },
    binStorageFee () {
      const binFee = this.defaultRates.storageFeeConfiguration.find(x => x.actionSourceId === 9)
      return binFee ? binFee.amount : 0
    },
    perPickFee () {
      const pickFee = this.defaultRates.perItemFeeConfiguration.find(x => x.amount !== 0)
      return pickFee ? pickFee.amount : 0
    },
    freeNumberOfPickInWord () {
      const freePick = this.defaultRates.perItemFeeConfiguration.find(x => x.amount === 0)
      return freePick ? this.numberInWords[freePick.endCount] : this.numberInWords[0]
    },
    wroFeeAfterHoursInWord () {
      const afterHours = this.defaultRates.wroReceivedFeeConfiguration.find(x => x.chargeAfterHours !== 0)
      return afterHours ? this.numberInWords[afterHours.chargeAfterHours] : this.numberInWords[0]
    },
    wroFeeBeforeHours () {
      const beforeHours = this.defaultRates.wroReceivedFeeConfiguration.find(x => x.chargeAfterHours === 0)
      return beforeHours ? beforeHours.amount : 0
    },
    wroFeeAfterHours () {
      const afterHours = this.defaultRates.wroReceivedFeeConfiguration.find(x => x.chargeAfterHours !== 0)
      return afterHours ? afterHours.amount : 0
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    }
  },
  created () {
    const homeCountry = this.$shipBobStore.getters['pricing/userHomeCountry']
    this.homeCountry = homeCountry
    this.$shipBobStore.dispatch('pricing/getDefaultRates', { countryId: homeCountry.countryId, currencyCode: homeCountry.currencyCode })
  }
}
</script>

<style lang="scss" scoped>

$icon-width: 2.5%;
$icon-width-small-screen: 100%;
$indent:  2%;

.pricing-breakdown-with-currency {
  color: #717988;

  &.small {
    font-size: 12px;

    .sb-panel-default {
      .pricing-breakdown-content {
        .panel-row {
          padding: 1em 0.75em;

          &.no-icon {
            margin-left: 0;
          }

          .row-icon {
            width: $icon-width-small-screen;
          }

          .row-text {
            width: $icon-width-small-screen;
            align-items: flex-start;

            .column-1 {
              width: 20%;
            }
          }

          .row-text:not(.row-indent) {
            .column-1 {
              padding-left: 0;
            }
          }
        }
      }
    }
  }

  &.large {
    font-size: 14px;

    .sb-panel-default {
      .pricing-breakdown-content {
        .panel-row {
          padding: 1em 1.25em;

          &.no-icon {
            margin-left: $icon-width;
          }

          .row-icon {
            width: $icon-width;
          }

          .row-text {
            width: calc(100% - #{$icon-width});
            align-items: center;

            .hide-large-screen {
              display: none;
            }
          }
        }
      }
    }
  }

  .sb-panel-default {
    padding: 0 !important;

    .pricing-breakdown-content {
      flex-direction: column;
      width: 100%;
      margin-top: 0.75em;

      .panel-row {
        display: flex;
        flex-direction: row;
        width: 100%;
        flex-wrap: wrap;

        .row-icon {
          display: flex;
          align-items: center;

          img.cart-icon {
            height: 21px;
            width: 20px;
          }

          img.storage-icon {
            height: 20px;
            width: 20px;
          }

          img.receiving-icon {
            height: 13px;
            width: 20px;
          }

          img.additional-services-icon {
            height: 18px;
            width: 18px;
          }
        }

        .row-text {
          display: flex;
          flex-direction: row;
          word-break: break-word;

          .help-link {
            font-weight: 600;
          }

          .column {
            padding: 0 0.2em;
          }

          .column-1 {
            width: 20%;
          }

          &.row-indent {
            .column-1 {
              padding-left: $indent;
            }
          }

          .column-2 {
            width: 25%;
          }

          .column-3 {
            width: 55%;
          }
        }
      }

      .padding-top-0 {
        padding-top: 0 !important;
      }

      .panel-h-separator {
        height: 1px;
        border-bottom: 1px solid #e5e5e5;
      }

      .heading {
        text-transform: uppercase;
      }
    }
  }

  .note {
    margin-top: 1em;
    width: 100%;
    font-style: italic;
  }
}
</style>
